import { useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { GestaoEpi } from "../../domain/models/gestao_epi";
import {
  initialCadastroEpiListaState,
  useCadastroEpiListaFilters,
} from "../hooks/CadastroEpi_lista/filters";
import { useCadastroEpiListaPagination } from "../hooks/CadastroEpi_lista/pagination";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { GestaoEpiForm } from "../schemas/CadastroEpi_lista/gestao_epi";
import {
  useCreateDebounceHandler,
  useDebouncedCallback,
} from "../../../../common/hooks/use_debounced_callback";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useTermoEpiListaPagination } from "../hooks/TermoEpi_lista/pagination";
import { TermoEpi } from "../../domain/models/termo_epi";
import { TermoEpiForm } from "../schemas/TermoEpi/termo_epi";
import { useTermoEpiListaFilters } from "../hooks/TermoEpi_lista/filters";

type LoadTermoEpiListaFuncState = {
  nome?: string | null;
  status?: number;
  page?: number;
  rowsPerPage?: number;
};

export const useTermoEpiListaViewModel = () => {
  const {
    gestaoEpi: {
      getAllTermoEPIUseCase,
      createTermoEPIUseCase,
      updateTermoEPIUseCase,
      deleteTermoEPIUseCase,
    },
  } = useDependencyInjection();

  const { pushInfoNotification, pushErrorNotification } = usePageLoader();

  const debouncedHandler = useCreateDebounceHandler(750);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [termoEpiDeleteModal, setTermoEpiDeleteModal] = useState(false);
  const [termoEpiCadastroModal, setTermoEpiCadastroModal] = useState(false);

  const [termoEpiLista, setTermoEpiLista] = useState<TermoEpi[]>();
  const [selectedTermoEpi, setSelectedTermoEpi] = useState<TermoEpi>();

  const filters = useTermoEpiListaFilters();
  const pagination = useTermoEpiListaPagination();

  //#region  Funcoes de carregamento

  const loadTermoEpiLista = useCallback(
    async (fs?: LoadTermoEpiListaFuncState) => {
      try {
        const filteredNome = evaluateParamValue(fs?.nome, filters.nome);
        const filteredStatus = evaluateParamValue(fs?.status, filters.status);

        const page = fs?.page ?? pagination.page;
        const rowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllTermoEPIUseCase.execute(
          undefined,
          undefined,
          filteredNome ?? "",
          filteredStatus ?? 2,
          page,
          rowsPerPage,
        );
        setTermoEpiLista(getAllTermoEPIUseCase.items);
        pagination.setTotalItems(getAllTermoEPIUseCase.totalItems);
      } catch (e: unknown) {
        setTermoEpiLista(undefined);
        throw new Error("Falha ao buscar lista de epis!");
      }
    },
    [filters.nome, filters.status, getAllTermoEPIUseCase, pagination],
  );

  //#endregion

  //#region  useEffect

  usePageStartBootstrap(async () => {
    try {
      await loadTermoEpiLista();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao iniciar pagina!")[0]);
    }
  });

  // #endregion

  //#region Eventos

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadTermoEpiLista();
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao recarregar pagina!"),
      );
    } finally {
      setLoading(false);
    }
  }, [loadTermoEpiLista, pushErrorNotification]);

  const handleTermoEpiModalOpenState = useCallback(
    (idTermoEpi: number | null, openState: boolean) => {
      if (openState) {
        if (termoEpiLista && idTermoEpi) {
          setSelectedTermoEpi(
            termoEpiLista.find((x) => x.idTermoEPI === idTermoEpi),
          );
        } else {
          setSelectedTermoEpi(undefined);
        }
      }
      setTermoEpiCadastroModal(openState);
    },
    [termoEpiLista],
  );

  const handleCadastroTermoEpiSubmit = useCallback(
    async (form: TermoEpiForm) => {
      try {
        setSaving(true);
        if (selectedTermoEpi) {
          await updateTermoEPIUseCase.execute(
            selectedTermoEpi.idTermoEPI,
            form.codigo,
            form.descricao,
            form.nome ?? undefined,
            form.ativo === 1 ? true : false,
          );
        } else {
          await createTermoEPIUseCase.execute(
            form.codigo,
            form.descricao,
            form.nome ?? undefined,
          );
        }
        setTermoEpiCadastroModal(false);
        setSelectedTermoEpi(undefined);
        await loadTermoEpiLista();
        pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao salvar registro!"),
        );
      } finally {
        setSaving(false);
      }
    },
    [
      selectedTermoEpi,
      loadTermoEpiLista,
      pushInfoNotification,
      updateTermoEPIUseCase,
      createTermoEPIUseCase,
      pushErrorNotification,
    ],
  );

  const handleDeleteTermoEpiModalOpenState = useCallback(
    (idTermoEpi: number | null, openState: boolean) => {
      if (openState) {
        if (termoEpiLista && idTermoEpi) {
          setSelectedTermoEpi(
            termoEpiLista.find((x) => x.idTermoEPI === idTermoEpi),
          );
        } else {
          setSelectedTermoEpi(undefined);
        }
      }
      setTermoEpiDeleteModal(openState);
    },
    [termoEpiLista],
  );

  const handleDeleteTermoEpiSubmit = useCallback(async () => {
    try {
      setSaving(true);
      if (selectedTermoEpi) {
        await deleteTermoEPIUseCase.execute(selectedTermoEpi.idTermoEPI);
      }
      await loadTermoEpiLista();
      pushInfoNotification("Sucesso", "Registro deletado com sucesso!");
    } catch (e: unknown) {
      pushErrorNotification("Erro", "");
    } finally {
      setSelectedTermoEpi(undefined);
      setSaving(false);
      setTermoEpiDeleteModal(false);
    }
  }, [
    selectedTermoEpi,
    loadTermoEpiLista,
    pushInfoNotification,
    deleteTermoEPIUseCase,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useCadastroEpiListaPagination.setState({ page: page + 1 });
        await loadTermoEpiLista({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao recarregar pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadTermoEpiLista, pushErrorNotification],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useCadastroEpiListaPagination.setState({ page: 1, rowsPerPage });
        await loadTermoEpiLista({ page: 1, rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao recarregar pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadTermoEpiLista, pushErrorNotification],
  );

  const handleNomeChange = useDebouncedCallback(
    debouncedHandler,
    async (nome: string | null) => {
      useTermoEpiListaFilters.setState({ nome });
      return async () => {
        try {
          setLoading(true);
          useCadastroEpiListaPagination.setState({ page: 1 });
          await loadTermoEpiLista({ nome, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao recarregar pagina!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [],
  );

  const handleStatusChange = useDebouncedCallback(
    debouncedHandler,
    (newValue: number | null) => {
      filters.setStatus(newValue ?? 2);
      return async () => {
        try {
          setLoading(true);
          pagination.setPage(1);
          await loadTermoEpiLista({ status: newValue ?? 2, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar termos!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [filters, pagination, loadTermoEpiLista, pushErrorNotification],
  );

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useCadastroEpiListaFilters.setState({ ...initialCadastroEpiListaState });
      useCadastroEpiListaPagination.setState({ page: 1 });
      await loadTermoEpiLista({ ...initialCadastroEpiListaState, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao recarregar pagina!"),
      );
    } finally {
      setLoading(false);
    }
  }, [loadTermoEpiLista, pushErrorNotification]);

  // #endregion

  return {
    loading,
    saving,
    errorMessage,
    termoEpiDeleteModal,
    termoEpiCadastroModal,
    termoEpiLista: termoEpiLista ?? [],
    selectedTermoEpi: selectedTermoEpi ?? null,
    filters,
    pagination,
    handleReload,
    handlePageChange,
    handleRowsPerPageChange,
    handleTermoEpiModalOpenState,
    handleCadastroTermoEpiSubmit,
    handleDeleteTermoEpiModalOpenState,
    handleDeleteTermoEpiSubmit,
    handleClearFilters,
    handleNomeChange,
    handleStatusChange
  };
};
